import { Component, Input, Output, EventEmitter, SimpleChanges, OnChanges } from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';
import { Observable } from 'rxjs';
import { ListResult } from '../../../api/core/list-result';

@Component({
  template: ''
})
export abstract class BasePickerComponent<T> implements ControlValueAccessor, OnChanges {
  @Input() multiple: boolean = false;
  @Input() disabled: boolean = false;
  @Input() useCache: boolean = true;
  @Input() placeholder: string = 'Type to search...';
  @Input() forceSelection: boolean = true;
  @Output() selectionChange = new EventEmitter<T | T[] | null>();

  suggestions: T[] = [];
  selected: T | T[] | undefined;

  // Internal change tracking
  onChange: any = () => { };
  onTouched: any = () => { };

  private readonly cache = new Map<string, T[]>();

  getItemKeyProperty = () => 'key';

  getItemLabelProperty = () => 'title';

  abstract search(query: string): Observable<ListResult<T>>;

  abstract load(keys: string[]): Observable<ListResult<T>>;

  format(items: T[]): T[] {
    return items;
  }

  ngOnChanges(changes: SimpleChanges) {
    // always clear cache when the input properties are changed
    this.cache.clear();
  }

  onLazyLoad(event: any) {
    let query = event?.query || '';

    const cacheKey = query || '(empty)';

    // Check cache first
    if (this.useCache && this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey)!;
      // must use shadow copy here, as the previous suggestions are kept in the autocomplete,
      // and wouldn't be considered as a change, as a result the component will always show the loading icon.
      this.suggestions = [...cached];
      return;
    }

    this.search(query).subscribe({
      next: (result) => {
        const items = this.format(result.items);
        this.ensureLabels(items);

        this.suggestions = items;
        if (this.useCache) {
          this.cache.set(cacheKey, this.suggestions);
        }
      },
      error: (err) => {
        this.suggestions = [];
        console.error('Failed to load data', err);
      }
    });
  }

  onSelect(autocomplete: any) {
    setTimeout(() => {
      const input = autocomplete.inputEL?.nativeElement;
      if (input) {
        input.setSelectionRange(0, 0);
        input.scrollLeft = 0;
      }
    }, 0);
  }

  onClear(event: any) {
    // optional: clear cache
    this.cache.clear();
  }

  handleChange(event: any) {
    // Only process actual object selections, ignore string inputs
    // This prevents unwanted auto-selections when user is typing
    if (event && typeof event === 'string') {
      // Ignore string events (user typing) to prevent unwanted selections
      return;
    }

    const keyProp = this.getItemKeyProperty();
    const key = this.selected
      ? (Array.isArray(this.selected)
        ? this.selected.map(x => (x as any)[keyProp])
        : (this.selected as any)[keyProp]
      )
      : undefined;

    this.onChange(key);
    this.onTouched();

    // Emit the selected object(s)
    this.selectionChange.emit(this.selected || null);
  }

  // Implementation of ControlValueAccessor interfaces
  writeValue(value: string | string[] | undefined): void {
    if (!value) {
      this.suggestions = [];
      this.selected = undefined;
      return;
    }

    const keys = Array.isArray(value) ? value : [value];
    if (!keys.length) {
      this.suggestions = [];
      this.selected = undefined;
      return;
    }

    this.load(keys).subscribe({
      next: (result) => {
        const matched = this.format(result.items);
        this.ensureLabels(matched);

        const keyProp = this.getItemKeyProperty();
        const matchedKeys = new Set(matched.map((x: any) => x[keyProp]));
        const missings = this.wrapErrorOptions(keys.filter(x => !matchedKeys.has(x)));

        // always include the missing items, so that the user can determine to remove them from the web UI or not
        this.suggestions = matched.concat(missings);
        this.selected = this.multiple ? this.suggestions : this.suggestions[0];
      },
      error: (err) => {
        // show the GUIDs for errors alternatively
        this.suggestions = this.wrapErrorOptions(keys);
        this.selected = this.multiple ? this.suggestions : this.suggestions[0];
        console.error('Failed to load data', err);
      }
    });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  wrapErrorOptions(keys: string[]): T[] {
    const keyProp = this.getItemKeyProperty();
    const labelProp = this.getItemLabelProperty();

    return keys.map(x => {
      const errOpt: any = {};

      errOpt[keyProp] = x;
      errOpt[labelProp] = x;

      return errOpt;
    });
  }

  ensureLabels(items: T[]): T[] {
    const labelProp = this.getItemLabelProperty();

    items.forEach((x: any) => {
      if (!x[labelProp]) {
        x[labelProp] = '(Empty)';
      }
    });

    return items;
  }
}
